import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Tabs,
  Tab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,

  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  EventBusy as EventBusyIcon,
  Timeline as TimelineIcon,
  CalendarToday as CalendarTodayIcon,
  BeachAccess as BeachAccessIcon,
  MedicalServices as MedicalIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import toast from 'react-hot-toast'
import { usePagination, useLoading, useDialog, useTableFilters } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'
import { exportToCSV } from '../../utils/exportUtils'
import { formatDate } from '../../utils/formatters'
import { leaveApi } from '../../api/leaveApi'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'

const LeaveHistory = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state) => state.auth)
  const { loading, withLoading } = useLoading()
  const pagination = usePagination()
  const dialog = useDialog()

  const leaveTypes = [
    { value: 'casual', label: 'Casual Leave' },
    { value: 'sick', label: 'Sick Leave' },
    { value: 'paid', label: 'Paid Leave' },
    { value: 'maternity', label: 'Maternity Leave' },
    { value: 'paternity', label: 'Paternity Leave' },
    { value: 'unpaid', label: 'Unpaid Leave' }
  ]

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedTab, setSelectedTab] = useState(0)

  const [leaveHistory, setLeaveHistory] = useState({
    summary: {
      totalLeaves: 0,
      approved: 0,
      pending: 0,
      rejected: 0,
      cancelled: 0,
      averageDaysPerLeave: 0,
      totalDaysTaken: 0,
    },
    detailedHistory: []
  })

  const [detailedLeaveData, setDetailedLeaveData] = useState(null)

  // We can use useTableFilters for standard filters, but we also have tabs.
  // Let's use manual filtering for tabs + useTableFilters for form filters.
  const { filters, updateFilter } = useTableFilters({
    status: 'all',
    type: 'all',
    search: '',
  })

  useEffect(() => {
    fetchLeaveHistory()
  }, [selectedYear, selectedTab])

  const fetchLeaveHistory = async () => {
    if (!user?.id) return; // Ensure we have employee ID

    withLoading(async () => {
      try {
        const response = await leaveApi.getLeaveHistory(user.id)
        const historyData = response.data.leave_history || []

        let totalLeaves = 0
        let approved = 0
        let pending = 0
        let rejected = 0
        let cancelled = 0
        let totalDaysTaken = 0
        let approvalRate = 0



        // Fetch real stats from API
        try {
          const statsResponse = await leaveApi.getLeaveStats(user.id)
          const statsData = statsResponse.data

          totalLeaves = statsData.total_leaves_applied || totalLeaves
          approved = statsData.approved || approved
          pending = statsData.pending || pending
          rejected = statsData.rejected || rejected
          cancelled = statsData.cancelled || cancelled
          approvalRate = statsData.approval_rate !== undefined ? statsData.approval_rate : approvalRate

          // Note: API doesn't seem to return total days taken, so we keep the calculated one for now
          // or if API adds it later, we can map it here.

        } catch (error) {
          console.error('Failed to fetch leave stats:', error)
        }

        // Helper to parse DD-MM-YYYY or standard dates
        const parseDate = (dateStr) => {
          if (!dateStr) return null

          // Check if it matches DD-MM-YYYY format
          if (typeof dateStr === 'string' && /^\d{2}-\d{2}-\d{4}/.test(dateStr)) {
            const [day, month, year] = dateStr.split('-').map(Number)
            // Note: Month is 0-indexed in Date constructor
            return new Date(year, month - 1, day)
          }

          const date = new Date(dateStr)
          return isNaN(date.getTime()) ? null : date
        }

        const mappedHistory = historyData.map(leave => {
          // Parse dates correctly using our utility logic implicitly or explicitly if needed for calculations
          // For calculations we might still need parsed dates, but for display we use formatDate

          // Re-using the logic from formatDate for consistent parsing if needed for diff calculation
          // However, since formatDate returns a string, we might need a parse utility in formatters or just use new Date if ISO.
          // But wait, the previous code had specific DD-MM-YYYY handling. 
          // Let's use the date strings directly for display with formatDate.

          // For calculation of days, we need Date objects. 
          // Let's recreate a local helper or better yet, trust the API 'duration' or 'days' if available, 
          // but the previous code calculated it.

          const parseDateForCalc = (dateStr) => {
            if (!dateStr) return null
            if (typeof dateStr === 'string' && /^\d{2}-\d{2}-\d{4}/.test(dateStr)) {
              const [day, month, year] = dateStr.split('-').map(Number)
              return new Date(year, month - 1, day)
            }
            const date = new Date(dateStr)
            return isNaN(date.getTime()) ? null : date
          }

          const startDate = parseDateForCalc(leave.start_date)
          const endDate = parseDateForCalc(leave.end_date)
          const appliedDate = parseDateForCalc(leave.applied_on) || parseDateForCalc(leave.created_at)

          // Calculate days for display
          let days = 0
          if (startDate && endDate) {
            const diffTime = Math.abs(endDate - startDate)
            days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
          } else if (leave.duration === 'full day') {
            days = 1
          } else if (leave.duration === 'half day') {
            days = 0.5
          }

          return {
            id: leave.id,
            leaveId: leave.leave_code,
            type: leave.leave_type,
            from: formatDate(leave.start_date), // Use global formatter
            to: formatDate(leave.end_date),     // Use global formatter
            fromDate: startDate,    // Keep for sorting
            toDate: endDate,        // Keep for sorting
            days: days,
            appliedOn: formatDate(appliedDate), // Use global formatter
            appliedDate: appliedDate, // Keep for sorting
            status: leave.status.charAt(0).toUpperCase() + leave.status.slice(1),
            reason: leave.reason,
            approvedBy: leave.approved_by_name || leave.approved_by || '-',
            approvedOn: formatDate(leave.approved_at), // Use global formatter
            remarks: leave.remarks || '-',
            cancellationRequested: false,
            createdAt: leave.created_at
          }
        }).sort((a, b) => {
          // Sort by applied date (newest first)
          const dateA = a.appliedDate || new Date(0)
          const dateB = b.appliedDate || new Date(0)
          return dateB - dateA
        })

        setLeaveHistory(prev => ({
          ...prev,
          summary: {
            totalLeaves,
            approved,
            pending,
            rejected,
            cancelled,
            averageDaysPerLeave: approved > 0 ? (totalDaysTaken / approved).toFixed(1) : 0,
            totalDaysTaken,
            approvalRate
          },
          detailedHistory: mappedHistory
        }))

      } catch (error) {
        console.error('Failed to fetch leave history:', error)
        toast.error('Failed to fetch leave history')
      }
    })
  }

  const handleExportData = () => {
    toast.success('Exporting leave history...')
    const columns = [
      { label: 'Leave ID', key: 'leaveId' },
      { label: 'Type', key: 'type' },
      { label: 'From Date', key: 'from' },
      { label: 'To Date', key: 'to' },
      { label: 'Days', key: 'days' },
      { label: 'Applied On', key: 'appliedOn' },
      { label: 'Status', key: 'status' },
      { label: 'Reason', key: 'reason' },
      { label: 'Approved By', key: 'approvedBy' },
      { label: 'Remarks', key: 'remarks' }
    ]
    exportToCSV(filteredLeaves, columns, 'my-leave-history')
  }



  const handleCancelRequest = async (leaveId) => {
    if (window.confirm('Are you sure you want to cancel this leave request?')) {
      withLoading(async () => {
        try {
          await leaveApi.cancelLeave(leaveId, { skipSuccessToast: true })
          toast.success('Leave request cancelled successfully')
          await fetchLeaveHistory() // Refresh data
        } catch (error) {
          console.error('Failed to cancel leave:', error)
        }
      })
    }
  }

  const fetchLeaveDetails = async (leaveId) => {
    try {
      const response = await leaveApi.getLeaveDetails(leaveId)
      setDetailedLeaveData(response.data)
      dialog.openDialog({ id: leaveId }) // Open dialog with leave ID
    } catch (error) {
      console.error('Failed to fetch leave details:', error)
      toast.error('Failed to load leave details')
    }
  }

  const handleCloseDialog = () => {
    dialog.closeDialog()
    setDetailedLeaveData(null) // Clear data when closing
  }

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue)
    pagination.handleChangePage(null, 0)
  }

  const calculateApprovalRate = () => {
    return leaveHistory.summary.approvalRate || 0
  }

  const getLeaveTypeIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'casual leave': return <BeachAccessIcon />
      case 'sick leave': return <MedicalIcon />
      case 'earned leave': return <CalendarTodayIcon />
      case 'unpaid leave': return <EventBusyIcon />
      case 'work from home': return <WorkIcon />
      default: return <BeachAccessIcon />
    }
  }

  const getFilteredLeaves = () => {
    let filtered = leaveHistory.detailedHistory

    // Filter by selected year
    filtered = filtered.filter(leave => {
      if (!leave.fromDate) return false
      const leaveYear = leave.fromDate.getFullYear()
      return leaveYear === selectedYear
    })

    // Filter by tab
    if (selectedTab === 1) {
      filtered = filtered.filter(leave => leave.status === 'Pending')
    } else if (selectedTab === 2) {
      filtered = filtered.filter(leave => leave.status === 'Approved')
    } else if (selectedTab === 3) {
      filtered = filtered.filter(leave => leave.status === 'Rejected' || leave.status === 'Cancelled')
    }

    // Apply additional filters
    if (filters.status !== 'all') {
      filtered = filtered.filter(leave => leave.status?.toLowerCase() === filters.status.toLowerCase())
    }

    if (filters.type !== 'all') {
      filtered = filtered.filter(leave => leave.type?.toLowerCase() === filters.type)
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(leave =>
        (leave.leaveId || '').toLowerCase().includes(searchLower) ||
        (leave.reason || '').toLowerCase().includes(searchLower) ||
        (leave.remarks || '').toLowerCase().includes(searchLower) ||
        (leave.type || '').toLowerCase().includes(searchLower)
      )
    }

    return filtered
  }

  const filteredLeaves = getFilteredLeaves()
  const paginatedLeaves = filteredLeaves.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  )

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Leave History
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Complete history of all your leave applications and approvals
              </Typography>
            </Box>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                        {leaveHistory.summary.totalLeaves}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">Total Leaves</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>{leaveHistory.summary.approved}</Typography>
                      <Typography variant="body2" color="textSecondary">Approved</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>{leaveHistory.summary.pending}</Typography>
                      <Typography variant="body2" color="textSecondary">Pending</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336' }}>{leaveHistory.summary.rejected}</Typography>
                      <Typography variant="body2" color="textSecondary">Rejected</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>{calculateApprovalRate()}%</Typography>
                      <Typography variant="body2" color="textSecondary">Approval Rate</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3' }}>{leaveHistory.summary.totalDaysTaken}</Typography>
                      <Typography variant="body2" color="textSecondary">Total Days</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>


          {/* Tabs */}
          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon fontSize="small" />
                    All Leaves
                    <Chip label={leaveHistory.detailedHistory.length} size="small" sx={{ ml: 1 }} />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PendingIcon fontSize="small" />
                    Pending
                    <Chip label={leaveHistory.detailedHistory.filter(l => l.status === 'Pending').length} size="small" color="warning" sx={{ ml: 1 }} />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon fontSize="small" />
                    Approved
                    <Chip label={leaveHistory.detailedHistory.filter(l => l.status === 'Approved').length} size="small" color="success" sx={{ ml: 1 }} />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CancelIcon fontSize="small" />
                    Rejected/Cancelled
                    <Chip label={leaveHistory.detailedHistory.filter(l => l.status === 'Rejected' || l.status === 'Cancelled').length} size="small" color="error" sx={{ ml: 1 }} />
                  </Box>
                }
              />
            </Tabs>
          </Paper>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flex: 1, pr: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} label="Status" onChange={(e) => updateFilter('status', e.target.value)}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                <InputLabel>Type</InputLabel>
                <Select value={filters.type} label="Type" onChange={(e) => updateFilter('type', e.target.value)}>
                  <MenuItem value="all">All Types</MenuItem>
                  {leaveTypes.map((type) => (<MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>))}
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  views={['year']}
                  label="Select Year"
                  value={new Date(selectedYear, 0, 1)}
                  onChange={(newValue) => setSelectedYear(newValue.getFullYear())}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  sx={{ minWidth: 120, flex: 1 }}
                />
              </LocalizationProvider>

              <TextField
                size="small"
                label="Search..."
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                sx={{ minWidth: 200, flex: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button startIcon={<RefreshIcon />} onClick={fetchLeaveHistory} disabled={loading} size="small" variant="outlined">Refresh</Button>
              <Button startIcon={<DownloadIcon />} onClick={handleExportData} variant="outlined" size="small">Export</Button>

              <Button variant="contained" startIcon={<CalendarIcon />} onClick={() => navigate('/employee/leave/apply')} size="small">Apply</Button>
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Detailed Leave History
          </Typography>

          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Leave ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Applied On</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableSkeleton columns={8} rows={pagination.rowsPerPage} />
                  ) : paginatedLeaves.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CalendarIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body1" color="textSecondary">
                          No leave records found
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Your leave applications will appear here
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedLeaves.map((leave) => (
                      <TableRow key={leave.id} hover>
                        <TableCell><Typography variant="body2" fontWeight="medium">{leave.leaveId}</Typography></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getLeaveTypeIcon(leave.type)}
                            <Typography variant="body2">{leave.type}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{leave.from} - {leave.to}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={`${leave.days} day${leave.days > 1 ? 's' : ''}`} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{leave.appliedOn}</TableCell>
                        <TableCell>
                          <StatusChip status={leave.status} />
                        </TableCell>
                        <TableCell>
                          {leave.approvedBy !== '-' ? (
                            <Box>
                              <Typography variant="body2">{leave.approvedBy}</Typography>
                              <Typography variant="caption" color="textSecondary">{leave.approvedOn}</Typography>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => fetchLeaveDetails(leave.id)}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {leave.status === 'Pending' && !leave.cancellationRequested && (
                              <Tooltip title="Cancel Request">
                                <IconButton size="small" onClick={() => handleCancelRequest(leave.id)} color="error">
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredLeaves.length}
              rowsPerPage={pagination.rowsPerPage}
              page={pagination.page}
              onPageChange={pagination.handleChangePage}
              onRowsPerPageChange={pagination.handleChangeRowsPerPage}
            />
          </>
        </Paper>

        <Dialog open={dialog.isOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          {detailedLeaveData && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Leave Details - {detailedLeaveData.leave_code}</Typography>
                  <StatusChip status={detailedLeaveData.other_info.status} />
                </Box>
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  {/* Leave Information */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">Leave Type</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ textTransform: 'capitalize', mb: 2 }}>
                      {detailedLeaveData.leave_info.leave_type}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">Duration</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {detailedLeaveData.leave_info.days_requested} days ({detailedLeaveData.leave_info.duration})
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">From Date</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {formatDate(detailedLeaveData.leave_info.from_date)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">To Date</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {formatDate(detailedLeaveData.leave_info.to_date)}
                    </Typography>
                  </Grid>

                  {/* Leave Balance */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Leave Balance</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip label={`Applied: ${detailedLeaveData.leave_info.leave_balance.applied_for}`} size="small" />
                      <Chip label={`Total: ${detailedLeaveData.leave_info.leave_balance.total}`} size="small" variant="outlined" />
                      <Chip label={`Remaining: ${detailedLeaveData.leave_info.leave_balance.remaining}`} size="small" color="success" />
                    </Box>
                  </Grid>

                  {/* Reason */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="textSecondary">Reason</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {detailedLeaveData.additional_info.reason}
                    </Typography>
                  </Grid>

                  {/* Work Handover */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">Work Handover To</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {detailedLeaveData.additional_info.work_handover_to_name}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">Emergency Contact</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {detailedLeaveData.additional_info.emergency_contact_number}
                    </Typography>
                  </Grid>

                  {/* Work Handover Notes */}
                  {detailedLeaveData.additional_info.work_handover_notes && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="textSecondary">Work Handover Notes</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {detailedLeaveData.additional_info.work_handover_notes}
                      </Typography>
                    </Grid>
                  )}

                  {/* Approval Information */}
                  {detailedLeaveData.other_info.status !== 'pending' && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="textSecondary">
                          {detailedLeaveData.other_info.status === 'rejected' ? 'Rejected By' : 'Approved By'}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {detailedLeaveData.other_info.approved_by_name}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="textSecondary">
                          {detailedLeaveData.other_info.status === 'rejected' ? 'Rejected At' : 'Approved At'}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatDate(detailedLeaveData.other_info.approved_at, 'dd/MM/yyyy')}
                        </Typography>
                      </Grid>
                      {detailedLeaveData.other_info.remarks && (
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="body2" color="textSecondary">Remarks</Typography>
                          <Typography variant="body1">{detailedLeaveData.other_info.remarks}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Paper>
    </Box>
  )
}

export default LeaveHistory